import { useCallback, useEffect, useRef, useState } from 'react';

import type { ConnaroEvent, ModerationFinding } from '../api/types.js';

/**
 * Connaro Chat WebSocket client (ARCHITECTURE.md §21, §47).
 *
 * Mirrors the gateway's protocol in `apps/api/src/ws/protocol.ts`. The Console
 * sends a `subscribe` frame naming a workspace; the SERVER decides whether that
 * is allowed. Nothing here is an access-control decision — a workspace id in
 * this file is a request, and the gateway re-verifies membership before it
 * subscribes to anything.
 *
 * The browser holds no credential: the HttpOnly session cookie rides along with
 * the WebSocket handshake, which is why there is no token anywhere below (§11).
 */

const PROTOCOL_VERSION = 1;

/** Close codes the gateway uses. Kept in sync with protocol.ts. */
const CLOSE = {
  UNAUTHENTICATED: 4401,
  SESSION_ENDED: 4402,
  PROTOCOL_ERROR: 4400,
  STALE: 4408,
  GOING_AWAY: 1001,
} as const;

export type ChatConnectionState =
  'idle' | 'connecting' | 'live' | 'reconnecting' | 'unauthorized' | 'signed_out' | 'error';

export type ChatSocket = {
  state: ChatConnectionState;
  /** Safe to show a customer; never a stack trace or provider detail (§48). */
  message: string | null;
  /** Events, oldest first, as delivered. Buffering is the caller's job. */
  onEvent: (handler: (event: ConnaroEvent) => void) => () => void;
  /** New moderation findings for this workspace (§98). */
  onFinding: (handler: (finding: ModerationFinding) => void) => () => void;
  /**
   * Whether the gateway is relaying findings to THIS socket.
   *
   * The server states it in the `subscribed` frame rather than leaving the
   * Console to infer it. An operator without `moderation.view` sees a queue
   * that never updates, and a silent queue is indistinguishable from a quiet
   * one — so the Moderator page says "not live" instead of implying it is.
   */
  moderationLive: boolean;
  reconnectNow: () => void;
};

/** Exponential backoff with full jitter, matching the server's approach (§43). */
function backoffMs(attempt: number): number {
  const ceiling = Math.min(1_000 * 2 ** Math.min(attempt - 1, 5), 30_000);
  return Math.round(Math.random() * ceiling);
}

function socketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/v1/ws`;
}

export function useChatSocket(workspaceId: string | null): ChatSocket {
  const [state, setState] = useState<ChatConnectionState>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlersRef = useRef(new Set<(event: ConnaroEvent) => void>());
  const findingHandlersRef = useRef(new Set<(finding: ModerationFinding) => void>());
  const [moderationLive, setModerationLive] = useState(false);
  /** Bumped to force a reconnect without changing the workspace. */
  const [nonce, setNonce] = useState(0);

  const onEvent = useCallback((handler: (event: ConnaroEvent) => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const onFinding = useCallback((handler: (finding: ModerationFinding) => void) => {
    findingHandlersRef.current.add(handler);
    return () => {
      findingHandlersRef.current.delete(handler);
    };
  }, []);

  const reconnectNow = useCallback(() => {
    attemptRef.current = 0;
    setNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    if (workspaceId === null) {
      setState('idle');
      setMessage(null);
      return;
    }

    let disposed = false;

    const clearRetry = (): void => {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const scheduleRetry = (): void => {
      if (disposed) return;

      attemptRef.current += 1;
      const delay = backoffMs(attemptRef.current);
      setState('reconnecting');
      clearRetry();
      retryTimerRef.current = setTimeout(connect, delay);
    };

    function connect(): void {
      if (disposed) return;

      setState((current) => (current === 'reconnecting' ? current : 'connecting'));

      let socket: WebSocket;
      try {
        socket = new WebSocket(socketUrl());
      } catch {
        scheduleRetry();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        // The gateway sends `welcome` first; subscribing immediately is
        // harmless because it re-authenticates on every subscribe anyway.
        socket.send(JSON.stringify({ v: PROTOCOL_VERSION, type: 'subscribe', workspaceId }));
      };

      socket.onmessage = (raw: MessageEvent<string>) => {
        let frame: unknown;
        try {
          frame = JSON.parse(raw.data);
        } catch {
          return;
        }

        if (typeof frame !== 'object' || frame === null || !('type' in frame)) return;
        const typed = frame as {
          type: string;
          event?: ConnaroEvent;
          finding?: ModerationFinding;
          moderation?: boolean;
          message?: string;
        };

        switch (typed.type) {
          case 'subscribed':
            attemptRef.current = 0;
            setState('live');
            setMessage(null);
            setModerationLive(typed.moderation === true);
            return;

          case 'moderation.finding': {
            const finding = typed.finding;
            if (finding === undefined) return;
            for (const handler of findingHandlersRef.current) handler(finding);
            return;
          }

          case 'event': {
            const event = typed.event;
            if (event === undefined) return;
            for (const handler of handlersRef.current) handler(event);
            return;
          }

          case 'error':
            // The gateway declines a workspace without closing the socket, so
            // reconnecting would loop on the same refusal.
            setState('unauthorized');
            setMessage(typed.message ?? 'You do not have access to this workspace.');
            return;

          default:
            return;
        }
      };

      socket.onclose = (event: CloseEvent) => {
        socketRef.current = null;
        // The claim "findings are live" belongs to the OPEN socket. Leaving it
        // true across a drop would tell an operator the queue is current while
        // nothing is arriving.
        setModerationLive(false);
        if (disposed) return;

        switch (event.code) {
          case CLOSE.UNAUTHENTICATED:
          case CLOSE.SESSION_ENDED:
            // Retrying with a cookie the server has already rejected would
            // hammer it to no purpose.
            setState('signed_out');
            setMessage('Your session ended. Sign in again to resume chat.');
            return;

          case CLOSE.PROTOCOL_ERROR:
            setState('error');
            setMessage('Connaro could not understand the chat connection. Reload the page.');
            return;

          default:
            scheduleRetry();
        }
      };

      socket.onerror = () => {
        // Always followed by `close`, which is where reconnection is decided.
      };
    }

    connect();

    return () => {
      disposed = true;
      clearRetry();
      const socket = socketRef.current;
      socketRef.current = null;
      socket?.close();
    };
    // `nonce` forces a fresh connection on demand; `workspaceId` on a switch.
  }, [workspaceId, nonce]);

  return { state, message, onEvent, onFinding, moderationLive, reconnectNow };
}
